import { useState, useEffect } from "react";
import { Heading, View, Card, Text, Button, Flex } from "@aws-amplify/ui-react";
import { useNavigate } from "react-router-dom";
import { getCurrentUser } from "aws-amplify/auth";
import { generateClient } from "aws-amplify/data";
import { useSubscriptionContext } from '../contexts/SubscriptionContext';
import PageContainer from "../components/PageContainer";
import OutdatedSharesWidget from "../components/OutdatedSharesWidget";
import QRCode from "qrcode";
import { useMockData } from "../contexts/MockDataContext";
import { useTranslation } from "react-i18next";

import type { Schema } from "@backend-schema";

function Home() {
  const { t } = useTranslation("common");
  const client = generateClient<Schema>();
  const navigate = useNavigate();
  const { isPro, subscriptionDetails } = useSubscriptionContext();
  const { mockEnabled, getMockData, getMockPackages } = useMockData();
  const [stats, setStats] = useState({
    skillPackages: 0,
    skillPackagesTotal: 0,
    completionPercentage: 0,
    certifications: 0,
    sharedSkills: 0,
    pendingShares: 0,
    skillCategories: 0,
    skills: 0,
    ratedSkills: 0,
  });
  const [publicShare, setPublicShare] = useState({
    enabled: false,
    url: "",
    id: "",
  });
  const [userProfile, setUserProfile] = useState({
    firstName: "",
    lastName: "",
  });
  const [expiringCerts, setExpiringCerts] = useState<
    { id: string; name: string; expiryDate?: string }[]
  >([]);
  const [pendingInvitations, setPendingInvitations] = useState<
    { id: string; senderName: string; recipientEmail: string; status: string }[]
  >([]);
  const [loading, setLoading] = useState(true);
  const [, setResize] = useState(0);

  // Check URL parameters for tab navigation
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get("tab") === "invitations") {
      // Clear the URL parameter after handling it
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []);

  useEffect(() => {
    async function fetchStats() {
      if (mockEnabled) {
        const mockPackages = getMockPackages();
        const mockCerts = getMockData().certifications;
        const totalSkills = mockPackages.reduce(
          (sum, pkg) => sum + pkg.skills.length,
          0,
        );
        const totalRatings = mockPackages.reduce(
          (sum, pkg) =>
            sum + pkg.skills.reduce((s, skill) => s + skill.rating, 0),
          0,
        );
        const avgCompletion = Math.round(
          (totalRatings / (totalSkills * 5)) * 100,
        );

        setStats({
          skillPackages: mockPackages.length,
          skillPackagesTotal: 0,
          completionPercentage: avgCompletion,
          certifications: mockCerts.length,
          sharedSkills: 3,
          pendingShares: 1,
          skillCategories: mockPackages.reduce((sum, pkg) => {
            const cats = new Set(pkg.skills.map((s) => s.category));
            return sum + cats.size;
          }, 0),
          skills: totalSkills,
          ratedSkills: totalSkills,
        });
        setLoading(false);
        return;
      }

      try {
        const userData = await getCurrentUser();

        // Fetch user profile info
        let attributes;
        try {
          const { fetchUserAttributes } = await import("aws-amplify/auth");
          attributes = await fetchUserAttributes();
          setUserProfile({
            firstName:
              attributes.given_name || attributes.name?.split(" ")[0] || "",
            lastName:
              attributes.family_name ||
              (attributes.name && attributes.name.split(" ").length > 1
                ? attributes.name.split(" ")[1]
                : "") ||
              "",
          });
        } catch (_authError) {
          // Ignore auth errors
          // Continue without user profile info
        }

        // Get user's skill packages (including custom ones)
        const allUserSkillPackages =
          await client.models.UserSkillPackages.list();
        const userSkillPackages = {
          data: allUserSkillPackages.data.filter(
            (pkg) => pkg.owner === userData.userId,
          ),
        };

        // Get custom skill packages
        const allCustomSkillPackages =
          await client.models.CustomSkillPackages.list();
        const customSkillPackages = {
          data: allCustomSkillPackages.data.filter(
            (pkg) => pkg.owner === userData.userId,
          ),
        };

        const totalSubscribedPackages =
          userSkillPackages.data.length + customSkillPackages.data.length;

        // Get user's certifications
        const allCertifications = await client.models.Certifications.list();
        const userCertifications = {
          data: allCertifications.data.filter(
            (cert) => cert.owner === userData.userId,
          ),
        };

        // Check for expiring certifications (within 6 months)
        const sixMonthsFromNow = new Date();
        sixMonthsFromNow.setMonth(sixMonthsFromNow.getMonth() + 6);

        const expiring = userCertifications.data.filter((cert) => {
          if (!cert.expiryDate) return false;
          const expiryDate = new Date(cert.expiryDate);
          const today = new Date();
          return expiryDate > today && expiryDate < sixMonthsFromNow;
        });

        setExpiringCerts(
          expiring.map((cert) => ({
            id: cert.id ?? "",
            name: cert.name,
            expiryDate: cert.expiryDate ?? undefined,
          })),
        );

        // Check for pending skill sharing invitations
        const userEmail = attributes?.email;
        console.log('🔍 [Home.tsx fetchStats] User email:', userEmail);
        console.log('🔍 [Home.tsx fetchStats] User username:', userData.username);

        if (userEmail) {
          try {
            console.log('📞 [Home.tsx fetchStats] Calling getRecipientInvitations...');
            // Use secure Lambda function to get received invitations
            const result = await client.queries.getRecipientInvitations({
              recipientEmail: userEmail,
              recipientUsername: userData.username,
            });

            console.log('📦 [Home.tsx fetchStats] Raw result from Lambda:', result);
            console.log('📦 [Home.tsx fetchStats] result.data:', result.data);
            console.log('📦 [Home.tsx fetchStats] result.data?.invitations:', result.data?.invitations);

            const rawInvitations = result.data?.invitations || [];
            console.log('📦 [Home.tsx fetchStats] Raw invitations count:', rawInvitations.length);
            console.log('📦 [Home.tsx fetchStats] Raw invitations:', rawInvitations);

            // Parse JSON strings to objects (Lambda returns JSON-serialized data)
            const receivedInvitations = rawInvitations
              .filter(
                (inv): inv is string => typeof inv === "string" && inv !== null,
              )
              .map((inv: string) => {
                try {
                  console.log('🔄 [Home.tsx fetchStats] Parsing invitation:', inv);
                  const parsed = JSON.parse(inv) as Schema["ShareInvitation"]["type"];
                  console.log('✅ [Home.tsx fetchStats] Parsed invitation:', parsed);
                  return parsed;
                } catch (error) {
                  console.error("❌ [Home.tsx fetchStats] Error parsing invitation:", error, inv);
                  return null;
                }
              })
              .filter(
                (
                  inv: Schema["ShareInvitation"]["type"] | null,
                ): inv is Schema["ShareInvitation"]["type"] => inv !== null,
              );

            console.log('📋 [Home.tsx fetchStats] Received invitations count:', receivedInvitations.length);
            console.log('📋 [Home.tsx fetchStats] Received invitations:', receivedInvitations);

            // Filter to show only pending invitations
            const pendingInvitations = receivedInvitations.filter(
              (inv) => inv.status === "pending",
            );

            console.log('⏳ [Home.tsx fetchStats] Pending invitations count:', pendingInvitations.length);
            console.log('⏳ [Home.tsx fetchStats] Pending invitations:', pendingInvitations);

            setPendingInvitations(
              pendingInvitations.map((inv) => ({
                id: inv.id ?? "",
                senderName: inv.senderName,
                recipientEmail: inv.recipientEmail,
                status: inv.status ?? "pending",
              })),
            );
            
            console.log('✅ [Home.tsx fetchStats] Set pending invitations state');
          } catch (error) {
            console.error("❌ [Home.tsx fetchStats] Error fetching received invitations:", error);
          }
        } else {
          console.log('⚠️ [Home.tsx fetchStats] No user email found, skipping invitation fetch');
        }

        // Get shared skills count if pro (only accepted invitations)
        let sharedCount = 0;
        let pendingSharesCount = 0;
        if (isPro) {
          const allSharedSkills = await client.models.ShareInvitation.list();
          
          // Count invitations where user is the sender (owner)
          const userShares = allSharedSkills.data.filter(
            (share) => share.owner === userData.userId,
          );
          const sentAccepted = userShares.filter(
            (share) => share.status === "accepted",
          ).length;
          const sentPending = userShares.filter(
            (share) => share.status === "pending",
          ).length;
          
          // Count invitations where user is the recipient (using email)
          const userEmail = attributes?.email;
          let receivedAccepted = 0;
          let receivedPending = 0;
          if (userEmail) {
            const receivedShares = allSharedSkills.data.filter(
              (share) => share.recipientEmail === userEmail,
            );
            receivedAccepted = receivedShares.filter(
              (share) => share.status === "accepted",
            ).length;
            receivedPending = receivedShares.filter(
              (share) => share.status === "pending",
            ).length;
          }
          
          // Total counts include both sent and received
          sharedCount = sentAccepted + receivedAccepted;
          pendingSharesCount = sentPending + receivedPending;
        }

        // Get public share status
        const publicShares = await client.models.PublicShare.list();
        const userShare = publicShares.data?.find(
          (share) => share && share.owner === userData.userId,
        );
        if (userShare && userShare.isEnabled) {
          const baseUrl = window.location.origin;
          setPublicShare({
            enabled: true,
            url: `${baseUrl}/profiles/${userShare.id}`,
            id: userShare.id ?? "",
          });
        }

        // Calculate completion percentage as average of all skill packages
        let completion = 0;
        let totalCategories = 0;
        let totalSkills = 0;
        let ratedSkills = 0;
        if (totalSubscribedPackages > 0) {
          const allSkillRatings = await client.models.SkillRating.list();
          const userSkillRatings = allSkillRatings.data.filter(
            (rating) => rating.owner === userData.userId,
          );

          // Get hidden categories and skills
          const allHiddenCategories =
            await client.models.HiddenCategories.list();
          const hiddenCategories = new Set(
            allHiddenCategories.data
              .filter((item) => item.owner === userData.userId)
              .map((item) => item.categoryId),
          );

          const allHiddenSkills = await client.models.HiddenSkills.list();
          const hiddenSkills = new Set(
            allHiddenSkills.data
              .filter((item) => item.owner === userData.userId)
              .map((item) => item.skillId),
          );

          // Get all skill packages with their skills to calculate individual package percentages
          const packageCompletions = [];

          // Calculate for regular skill packages
          for (const userPkg of userSkillPackages.data) {
            const skillPackage = await client.models.SkillPackages.get({
              id: userPkg.skillPackageId!,
            });
            if (skillPackage?.data) {
              const categories = await client.models.SkillCategories.list({
                filter: {
                  skillPackageId: { eq: skillPackage.data?.id as string },
                },
              });

              const categoryCompletions = [];

              for (const category of categories.data) {
                // Skip hidden categories
                if (category.id && hiddenCategories.has(category.id)) continue;

                totalCategories++;

                const skills = await client.models.Skills.list({
                  filter: { categoryId: { eq: category.id! } },
                });

                // Filter out hidden skills
                const visibleSkills = skills.data.filter(
                  (skill) => skill.id && !hiddenSkills.has(skill.id),
                );
                totalSkills += visibleSkills.length;

                let categoryTotalRating = 0;
                const categorySkillCount = visibleSkills.length;

                for (const skill of visibleSkills) {
                  const skillKey = skill.name?.toLowerCase().replace(/[^a-z0-9]/g, '-');
                  const rating = userSkillRatings.find(
                    (r) => r.skillId === (skill.id || "") || r.skillKey === skillKey,
                  );
                  categoryTotalRating += rating ? rating.rating : 0; // Unrated = 0
                  if (rating && rating.rating > 0) ratedSkills++;
                }

                if (categorySkillCount > 0) {
                  const categoryCompletion =
                    (categoryTotalRating / (categorySkillCount * 5)) * 100;
                  categoryCompletions.push(categoryCompletion);
                }
              }

              if (categoryCompletions.length > 0) {
                const packageCompletion =
                  categoryCompletions.reduce((sum, comp) => sum + comp, 0) /
                  categoryCompletions.length;
                packageCompletions.push(packageCompletion);
              }
            }
          }

          // Calculate for custom skill packages
          for (const customPkg of customSkillPackages.data) {
            const allCategories =
              await client.models.CustomSkillCategories.list();
            const categories = {
              data: allCategories.data.filter(
                (cat) => cat.customPackageId === customPkg.id!,
              ),
            };

            const categoryCompletions = [];

            for (const category of categories.data) {
              // Skip hidden categories
              if (category.id && hiddenCategories.has(category.id)) continue;

              totalCategories++;

              const allSkills = await client.models.CustomSkills.list({ limit: 1000 });
              const categorySkills = allSkills.data.filter(
                (skill) => skill.customCategoryId === category.id!,
              );

              // Filter out hidden skills
              const visibleSkills = categorySkills.filter((skill) => {
                const skillId = skill.existingSkillId ?? skill.id ?? "";
                return !hiddenSkills.has(skillId);
              });
              totalSkills += visibleSkills.length;

              let categoryTotalRating = 0;
              const categorySkillCount = visibleSkills.length;

              for (const skill of visibleSkills) {
                  // For custom skills, check by existingSkillId if it exists
                  const skillId = skill.existingSkillId || skill.id;
                  const skillKey = skill.name?.toLowerCase().replace(/[^a-z0-9]/g, '-');
                  const rating = userSkillRatings.find(
                    (r) => r.skillId === skillId || r.skillKey === skillKey,
                  );
                  categoryTotalRating += rating ? rating.rating : 0; // Unrated = 0
                  if (rating && rating.rating > 0) ratedSkills++;
                }

              if (categorySkillCount > 0) {
                const categoryCompletion =
                  (categoryTotalRating / (categorySkillCount * 5)) * 100;
                categoryCompletions.push(categoryCompletion);
              }
            }

            if (categoryCompletions.length > 0) {
              const packageCompletion =
                categoryCompletions.reduce((sum, comp) => sum + comp, 0) /
                categoryCompletions.length;
              packageCompletions.push(packageCompletion);
            }
          }

          // Average all package completions
          if (packageCompletions.length > 0) {
            completion = Math.round(
              packageCompletions.reduce((sum, comp) => sum + comp, 0) /
                packageCompletions.length,
            );
          }
        }

        setStats({
          skillPackages: totalSubscribedPackages,
          skillPackagesTotal: 0, // Not needed anymore
          completionPercentage: completion,
          certifications: userCertifications.data.length,
          sharedSkills: sharedCount,
          pendingShares: pendingSharesCount,
          skillCategories: totalCategories,
          skills: totalSkills,
          ratedSkills: ratedSkills,
        });
      } catch {
        // Ignore errors when fetching stats
      } finally {
        setLoading(false);
      }
    }

    fetchStats();
  }, [isPro, mockEnabled, getMockData, getMockPackages]);

  // Refresh data when window gains focus (user returns from other pages)
  useEffect(() => {
    const handleFocus = async () => {
      try {
        const userData = await getCurrentUser();
        const { fetchUserAttributes } = await import("aws-amplify/auth");
        const attributes = await fetchUserAttributes();

        // Get shared skills count if pro (only accepted invitations)
        let sharedCount = 0;
        let pendingSharesCount = 0;
        if (isPro) {
          const allSharedSkills = await client.models.ShareInvitation.list();
          
          // Count invitations where user is the sender (owner)
          const userShares = allSharedSkills.data.filter(
            (share) => share.owner === userData.userId,
          );
          const sentAccepted = userShares.filter(
            (share) => share.status === "accepted",
          ).length;
          const sentPending = userShares.filter(
            (share) => share.status === "pending",
          ).length;
          
          // Count invitations where user is the recipient (using email)
          const userEmail = attributes?.email;
          let receivedAccepted = 0;
          let receivedPending = 0;
          if (userEmail) {
            const receivedShares = allSharedSkills.data.filter(
              (share) => share.recipientEmail === userEmail,
            );
            receivedAccepted = receivedShares.filter(
              (share) => share.status === "accepted",
            ).length;
            receivedPending = receivedShares.filter(
              (share) => share.status === "pending",
            ).length;
          }
          
          // Total counts include both sent and received
          sharedCount = sentAccepted + receivedAccepted;
          pendingSharesCount = sentPending + receivedPending;

          setStats((prev) => ({
            ...prev,
            sharedSkills: sharedCount,
            pendingShares: pendingSharesCount,
          }));
        }
      } catch {
        // Ignore errors on focus refresh
      }
    };

    window.addEventListener("focus", handleFocus);
    return () => window.removeEventListener("focus", handleFocus);
  }, [isPro, client.models.ShareInvitation]);

  // Handle resize to update tile width detection
  useEffect(() => {
    const handleResize = () => setResize(Date.now());
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Check for new invitations periodically
  useEffect(() => {
    const checkInvitations = async () => {
      try {
        const { fetchUserAttributes, getCurrentUser } = await import("aws-amplify/auth");
        const userAttributes = await fetchUserAttributes();
        const userEmail = userAttributes.email;
        const userData = await getCurrentUser();
        
        console.log('🔄 [Home.tsx periodic] Checking invitations...');
        console.log('🔄 [Home.tsx periodic] User email:', userEmail);
        console.log('🔄 [Home.tsx periodic] User username:', userData.username);

        if (userEmail) {
          try {
            console.log('📞 [Home.tsx periodic] Calling getRecipientInvitations...');
            // Use secure Lambda function to get received invitations
            const result = await client.queries.getRecipientInvitations({
              recipientEmail: userEmail,
              recipientUsername: userData.username,
            });

            console.log('📦 [Home.tsx periodic] Raw result:', result);
            const rawInvitations = result.data?.invitations || [];
            console.log('📦 [Home.tsx periodic] Raw invitations count:', rawInvitations.length);

            // Parse JSON strings to objects (Lambda returns JSON-serialized data)
            const receivedInvitations = rawInvitations
              .filter(
                (inv): inv is string => typeof inv === "string" && inv !== null,
              )
              .map((inv: string) => {
                try {
                  const parsed = JSON.parse(inv) as Schema["ShareInvitation"]["type"];
                  console.log('✅ [Home.tsx periodic] Parsed invitation:', parsed);
                  return parsed;
                } catch (error) {
                  console.error("❌ [Home.tsx periodic] Error parsing invitation:", error, inv);
                  return null;
                }
              })
              .filter(
                (
                  inv: Schema["ShareInvitation"]["type"] | null,
                ): inv is Schema["ShareInvitation"]["type"] => inv !== null,
              );

            console.log('📋 [Home.tsx periodic] Received invitations count:', receivedInvitations.length);

            // Filter to show only pending invitations
            const pendingInvitations = receivedInvitations.filter(
              (inv) => inv.status === "pending",
            );

            console.log('⏳ [Home.tsx periodic] Pending invitations count:', pendingInvitations.length);

            setPendingInvitations(
              pendingInvitations.map((inv) => ({
                id: inv.id ?? "",
                senderName: inv.senderName,
                recipientEmail: inv.recipientEmail,
                status: inv.status ?? "pending",
              })),
            );
            
            console.log('✅ [Home.tsx periodic] Updated pending invitations state');
          } catch (error) {
            console.error("❌ [Home.tsx periodic] Error fetching received invitations:", error);
          }
        } else {
          console.log('⚠️ [Home.tsx periodic] No user email found');
        }
      } catch (_error) {
        console.error('❌ [Home.tsx periodic] Error in checkInvitations:', _error);
      }
    };

    const interval = setInterval(checkInvitations, 30000); // Check every 30 seconds
    return () => clearInterval(interval);
  }, [client.queries]);

  return (
    <PageContainer>
      <style>{`
        @keyframes dropIn {
          0% {
            opacity: 0;
            transform: translateY(-30px);
          }
          100% {
            opacity: 1;
            transform: translateY(0);
          }
        }
        @keyframes pulse {
          0%, 100% {
            opacity: 1;
          }
          50% {
            opacity: 0.7;
          }
        }
        .drop-in-1 { animation: ${loading ? "none" : "dropIn 0.6s ease-out 0.2s both"}; }
        .drop-in-2 { animation: ${loading ? "none" : "dropIn 0.6s ease-out 0.4s both"}; }
        .drop-in-3 { animation: ${loading ? "none" : "dropIn 0.6s ease-out 0.6s both"}; }
        .drop-in-4 { animation: ${loading ? "none" : "dropIn 0.6s ease-out 0.8s both"}; }
        .cert-alert { animation: ${loading ? "none" : "dropIn 0.8s ease-out 1.0s both"}; }
        
        .hover-text {
          opacity: 0;
          transition: opacity 0.3s ease;
        }
        
        *:hover .hover-text {
          opacity: 1 !important;
        }
      `}</style>
      <Heading level={1} marginBottom="1rem">
        {t("dashboard.title")}
      </Heading>

      {/* Outdated Shares Widget */}
      {isPro && <OutdatedSharesWidget />}

      {/* Skill Sharing Notifications */}
      {pendingInvitations.length > 0 && (
        <Card
          variation="outlined"
          marginBottom="2rem"
          style={{
            background: "linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)",
            color: "white",
            border: "none",
          }}
        >
          <Flex alignItems="center" gap="1rem" padding="1.5rem">
            <Text fontSize="2rem">📩</Text>
            <View flex="1">
              <Text
                fontSize="1.1rem"
                fontWeight="bold"
                color="white"
                marginBottom="0.25rem"
              >
                {pendingInvitations.length} Skill
                {pendingInvitations.length > 1 ? "s" : ""} Shared With You
              </Text>
              <Text fontSize="0.9rem" color="rgba(255, 255, 255, 0.9)">
                {pendingInvitations.map((inv) => inv.senderName).join(", ")}{" "}
                shared skills with you
              </Text>
            </View>
            <Button
              variation="primary"
              onClick={() => {
                if (isPro) {
                  navigate("/app/shared-skills");
                } else {
                  navigate("/settings/subscription");
                }
              }}
              style={{
                background: "rgba(255, 255, 255, 0.2)",
                border: "2px solid rgba(255, 255, 255, 0.3)",
                color: "white",
              }}
            >
              {isPro ? "View Invitations" : "Upgrade to View"}
            </Button>
          </Flex>
        </Card>
      )}

      {/* Profile Completion and Trial Status Tiles */}
      {(!loading && !userProfile.firstName) ||
      (subscriptionDetails?.status === "trialing" &&
        subscriptionDetails?.trialEnd) ||
      (!isPro && subscriptionDetails?.status !== "trialing") ? (
        <Flex direction="row" gap="2rem" wrap="wrap" marginBottom="2rem">
          {/* Profile Completion Tile */}
          {!loading && !userProfile.firstName && (
            <View flex="1" minWidth="300px">
              <View
                style={{
                  perspective: "1000px",
                  width: "100%",
                  height: "120px",
                }}
              >
                <View
                  ref={(el) => {
                    if (el) {
                      const isWide = el.offsetWidth > 600;
                      el.setAttribute("data-wide", isWide.toString());
                    }
                  }}
                  style={{
                    position: "relative",
                    width: "100%",
                    height: "100%",
                    transformStyle: "preserve-3d",
                    transition: "transform 0.6s",
                    cursor: "pointer",
                  }}
                  onClick={() => navigate("/settings/profile")}
                  onMouseEnter={(e) => {
                    const isWide =
                      e.currentTarget.getAttribute("data-wide") === "true";
                    if (isWide) {
                      e.currentTarget.style.transform =
                        "scale(1.02) translateY(-5px)";
                    } else {
                      e.currentTarget.style.transform = "rotateY(180deg)";
                    }
                  }}
                  onMouseLeave={(e) => {
                    const isWide =
                      e.currentTarget.getAttribute("data-wide") === "true";
                    if (isWide) {
                      e.currentTarget.style.transform =
                        "scale(1) translateY(0)";
                    } else {
                      e.currentTarget.style.transform = "rotateY(0deg)";
                    }
                  }}
                >
                  {/* Front Side */}
                  <View
                    style={{
                      position: "absolute",
                      width: "100%",
                      height: "100%",
                      backfaceVisibility: "hidden",
                      background:
                        "linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)",
                      borderRadius: "16px",
                      padding: "1.5rem",
                      boxShadow: "0 8px 25px rgba(59, 130, 246, 0.2)",
                      display: "flex",
                      alignItems: "center",
                      gap: "1rem",
                    }}
                  >
                    <div
                      style={{
                        fontSize: "2.5rem",
                        marginBottom: "1rem",
                        padding: "1rem",
                        borderRadius: "50%",
                        background: "rgba(255, 255, 255, 0.2)",
                        width: "80px",
                        height: "80px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <span style={{ filter: "brightness(0) invert(1)" }}>
                        👋
                      </span>
                    </div>
                    <View flex="1">
                      <Text
                        fontSize="1.1rem"
                        fontWeight="bold"
                        color="white"
                        marginBottom="0.25rem"
                      >
                        Complete Your Profile
                      </Text>
                      <Text fontSize="0.85rem" color="rgba(255, 255, 255, 0.9)">
                        Add your details to get started
                      </Text>
                    </View>
                    {/* Show back content inline for wide tiles */}
                    <View
                      ref={(el) => {
                        if (el) {
                          const parent = el.closest('[data-wide="true"]');
                          el.style.display = parent ? "flex" : "none";
                        }
                      }}
                      style={{
                        alignItems: "center",
                        gap: "1rem",
                        borderLeft: "1px solid rgba(255, 255, 255, 0.3)",
                        paddingLeft: "1rem",
                        marginLeft: "1rem",
                      }}
                    >
                      <Text fontSize="2rem">⚙️</Text>
                      <View>
                        <Text
                          fontSize="1rem"
                          fontWeight="bold"
                          color="white"
                          marginBottom="0.25rem"
                        >
                          Setup Profile
                        </Text>
                        <Text
                          fontSize="0.8rem"
                          color="rgba(255, 255, 255, 0.9)"
                        >
                          Click to complete your profile setup
                        </Text>
                      </View>
                    </View>
                  </View>

                  {/* Back Side */}
                  <View
                    style={{
                      position: "absolute",
                      width: "100%",
                      height: "100%",
                      backfaceVisibility: "hidden",
                      background:
                        "linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)",
                      borderRadius: "16px",
                      padding: "1.5rem",
                      boxShadow: "0 8px 25px rgba(6, 182, 212, 0.2)",
                      display: "flex",
                      alignItems: "center",
                      gap: "1rem",
                      transform: "rotateY(180deg)",
                    }}
                  >
                    <Text fontSize="2rem">⚙️</Text>
                    <View flex="1">
                      <Text
                        fontSize="1.1rem"
                        fontWeight="bold"
                        color="white"
                        marginBottom="0.25rem"
                      >
                        Setup Profile
                      </Text>
                      <Text fontSize="0.85rem" color="rgba(255, 255, 255, 0.9)">
                        Click to complete your profile setup
                      </Text>
                    </View>
                  </View>
                </View>
              </View>
            </View>
          )}

          {/* Trial Status Tile */}
          {subscriptionDetails?.status === "trialing" &&
            subscriptionDetails?.trialEnd && (
              <View flex="1" minWidth="300px">
                <View
                  style={{
                    perspective: "1000px",
                    width: "100%",
                    height: "120px",
                  }}
                >
                  <View
                    ref={(el) => {
                      if (el) {
                        const isWide = el.offsetWidth > 600;
                        el.setAttribute("data-wide", isWide.toString());
                      }
                    }}
                    style={{
                      position: "relative",
                      width: "100%",
                      height: "100%",
                      transformStyle: "preserve-3d",
                      transition: "transform 0.6s",
                      cursor: "pointer",
                    }}
                    onClick={() => navigate("/settings/subscription")}
                    onMouseEnter={(e) => {
                      const isWide =
                        e.currentTarget.getAttribute("data-wide") === "true";
                      if (isWide) {
                        e.currentTarget.style.transform =
                          "scale(1.02) translateY(-5px)";
                      } else {
                        e.currentTarget.style.transform = "rotateY(180deg)";
                      }
                    }}
                    onMouseLeave={(e) => {
                      const isWide =
                        e.currentTarget.getAttribute("data-wide") === "true";
                      if (isWide) {
                        e.currentTarget.style.transform =
                          "scale(1) translateY(0)";
                      } else {
                        e.currentTarget.style.transform = "rotateY(0deg)";
                      }
                    }}
                  >
                    {/* Front Side */}
                    <View
                      style={{
                        position: "absolute",
                        width: "100%",
                        height: "100%",
                        backfaceVisibility: "hidden",
                        background:
                          "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                        borderRadius: "16px",
                        padding: "1.5rem",
                        boxShadow: "0 8px 25px rgba(245, 158, 11, 0.2)",
                        display: "flex",
                        alignItems: "center",
                        gap: "1rem",
                      }}
                    >
                      <Text fontSize="2rem">⏰</Text>
                      <View flex="1">
                        <Text
                          fontSize="1.1rem"
                          fontWeight="bold"
                          color="white"
                          marginBottom="0.25rem"
                        >
                          {t("dashboard.subscription.trial.title")}:{" "}
                          {Math.ceil(
                            (new Date(subscriptionDetails.trialEnd).getTime() -
                              new Date().getTime()) /
                              (1000 * 60 * 60 * 24),
                          )}{" "}
                          {t("dashboard.subscription.trial.daysRemaining", {
                            count: Math.ceil(
                              (new Date(
                                subscriptionDetails.trialEnd,
                              ).getTime() -
                                new Date().getTime()) /
                                (1000 * 60 * 60 * 24),
                            ),
                          })
                            .split(" ")
                            .slice(-2)
                            .join(" ")}
                        </Text>
                        <Text
                          fontSize="0.85rem"
                          color="rgba(255, 255, 255, 0.9)"
                        >
                          Ends{" "}
                          {new Date(
                            subscriptionDetails.trialEnd,
                          ).toLocaleDateString()}
                        </Text>
                      </View>

                      {/* Show back content inline for wide tiles */}
                      <View
                        ref={(el) => {
                          if (el) {
                            const parent = el.closest('[data-wide="true"]');
                            el.style.display = parent ? "flex" : "none";
                          }
                        }}
                        style={{
                          alignItems: "center",
                          gap: "1rem",
                          borderLeft: "1px solid rgba(255, 255, 255, 0.3)",
                          paddingLeft: "1rem",
                          marginLeft: "1rem",
                        }}
                      >
                        <Text fontSize="2rem">💳</Text>
                        <View>
                          <Text
                            fontSize="1rem"
                            fontWeight="bold"
                            color="white"
                            marginBottom="0.25rem"
                          >
                            {t("dashboard.subscription.trial.description")}
                          </Text>
                          <Text
                            fontSize="0.8rem"
                            color="rgba(255, 255, 255, 0.9)"
                          >
                            {t("dashboard.subscription.trial.description")}
                          </Text>
                        </View>
                      </View>
                    </View>

                    {/* Back Side */}
                    <View
                      style={{
                        position: "absolute",
                        width: "100%",
                        height: "100%",
                        backfaceVisibility: "hidden",
                        background:
                          "linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)",
                        borderRadius: "16px",
                        padding: "1.5rem",
                        boxShadow: "0 8px 25px rgba(220, 38, 38, 0.2)",
                        display: "flex",
                        alignItems: "center",
                        gap: "1rem",
                        transform: "rotateY(180deg)",
                      }}
                    >
                      <Text fontSize="2rem">💳</Text>
                      <View flex="1">
                        <Text
                          fontSize="1.1rem"
                          fontWeight="bold"
                          color="white"
                          marginBottom="0.25rem"
                        >
                          Manage Subscription
                        </Text>
                        <Text
                          fontSize="0.85rem"
                          color="rgba(255, 255, 255, 0.9)"
                        >
                          Click to manage your trial and billing
                        </Text>
                      </View>
                    </View>
                  </View>
                </View>
              </View>
            )}

          {/* Start Trial Tile */}
          {!isPro && subscriptionDetails?.status !== "trialing" && (
            <View flex="1" minWidth="300px">
              <View
                style={{
                  perspective: "1000px",
                  width: "100%",
                  height: "120px",
                }}
              >
                <View
                  ref={(el) => {
                    if (el) {
                      const isWide = el.offsetWidth > 600;
                      el.setAttribute("data-wide", isWide.toString());
                    }
                  }}
                  style={{
                    position: "relative",
                    width: "100%",
                    height: "100%",
                    transformStyle: "preserve-3d",
                    transition: "transform 0.6s",
                    cursor: "pointer",
                  }}
                  onClick={() => navigate("/settings/subscription")}
                  onMouseEnter={(e) => {
                    const isWide =
                      e.currentTarget.getAttribute("data-wide") === "true";
                    if (isWide) {
                      e.currentTarget.style.transform =
                        "scale(1.02) translateY(-5px)";
                    } else {
                      e.currentTarget.style.transform = "rotateY(180deg)";
                    }
                  }}
                  onMouseLeave={(e) => {
                    const isWide =
                      e.currentTarget.getAttribute("data-wide") === "true";
                    if (isWide) {
                      e.currentTarget.style.transform =
                        "scale(1) translateY(0)";
                    } else {
                      e.currentTarget.style.transform = "rotateY(0deg)";
                    }
                  }}
                >
                  {/* Front Side - or combined view for wide tiles */}
                  <View
                    style={{
                      position: "absolute",
                      width: "100%",
                      height: "100%",
                      backfaceVisibility: "hidden",
                      background:
                        "linear-gradient(135deg, #10b981 0%, #059669 100%)",
                      borderRadius: "16px",
                      padding: "1.5rem",
                      boxShadow: "0 8px 25px rgba(16, 185, 129, 0.2)",
                      display: "flex",
                      alignItems: "center",
                      gap: "1rem",
                    }}
                  >
                    <div
                      style={{
                        fontSize: "2.5rem",
                        marginBottom: "1rem",
                        padding: "1rem",
                        borderRadius: "50%",
                        background: "rgba(255, 255, 255, 0.2)",
                        width: "80px",
                        height: "80px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <span style={{ filter: "brightness(0) invert(1)" }}>
                        🚀
                      </span>
                    </div>
                    <View flex="1">
                      <Text
                        fontSize="1.1rem"
                        fontWeight="bold"
                        color="white"
                        marginBottom="0.25rem"
                      >
                        {t("dashboard.subscription.startTrial.title")}
                      </Text>
                      <Text fontSize="0.85rem" color="rgba(255, 255, 255, 0.9)">
                        {t("dashboard.subscription.startTrial.description")}
                      </Text>
                    </View>
                    {/* Show back content inline for wide tiles */}
                    <View
                      ref={(el) => {
                        if (el) {
                          const parent = el.closest('[data-wide="true"]');
                          el.style.display = parent ? "flex" : "none";
                        }
                      }}
                      style={{
                        alignItems: "center",
                        gap: "1rem",
                        borderLeft: "1px solid rgba(255, 255, 255, 0.3)",
                        paddingLeft: "1rem",
                        marginLeft: "1rem",
                      }}
                    >
                      <Text fontSize="2rem">✨</Text>
                      <View>
                        <Text
                          fontSize="1rem"
                          fontWeight="bold"
                          color="white"
                          marginBottom="0.25rem"
                        >
                          {t("dashboard.subscription.startTrial.backTitle")}
                        </Text>
                        <Text
                          fontSize="0.8rem"
                          color="rgba(255, 255, 255, 0.9)"
                        >
                          {t(
                            "dashboard.subscription.startTrial.backDescription",
                          )}
                        </Text>
                      </View>
                    </View>
                  </View>

                  {/* Back Side */}
                  <View
                    style={{
                      position: "absolute",
                      width: "100%",
                      height: "100%",
                      backfaceVisibility: "hidden",
                      background:
                        "linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)",
                      borderRadius: "16px",
                      padding: "1.5rem",
                      boxShadow: "0 8px 25px rgba(139, 92, 246, 0.2)",
                      display: "flex",
                      alignItems: "center",
                      gap: "1rem",
                      transform: "rotateY(180deg)",
                    }}
                  >
                    <Text fontSize="2rem">✨</Text>
                    <View flex="1">
                      <Text
                        fontSize="1.1rem"
                        fontWeight="bold"
                        color="white"
                        marginBottom="0.25rem"
                      >
                        {t("dashboard.subscription.startTrial.backTitle")}
                      </Text>
                      <Text fontSize="0.85rem" color="rgba(255, 255, 255, 0.9)">
                        {t("dashboard.subscription.startTrial.backDescription")}
                      </Text>
                    </View>
                  </View>
                </View>
              </View>
            </View>
          )}
        </Flex>
      ) : null}

      <Flex direction="row" gap="2rem" wrap="wrap">
        {/* Skill Packages Widget */}
        <View flex="1" minWidth="300px">
          <Flex
            direction="column"
            alignItems="center"
            justifyContent="flex-start"
            height="100%"
          >
            {loading ? (
              <View textAlign="center" width="100%">
                <View
                  style={{
                    width: "100%",
                    height: "240px",
                    marginBottom: "1.5rem",
                    background:
                      "linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%)",
                    borderRadius: "20px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    animation: "pulse 2s infinite",
                  }}
                >
                  <Text color="var(--amplify-colors-font-secondary)">
                    {t("dashboard.loading.skills")}
                  </Text>
                </View>

                <Flex direction="column" gap="1rem">
                  {[1, 2, 3, 4].map((i) => (
                    <View
                      key={i}
                      style={{
                        background:
                          "linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)",
                        borderRadius: "12px",
                        padding: "1rem",
                        border: "1px solid #e2e8f0",
                        animation: `pulse 2s infinite ${i * 0.2}s`,
                      }}
                    >
                      <Flex justifyContent="space-between" alignItems="center">
                        <View
                          style={{
                            width: "60px",
                            height: "12px",
                            background: "#cbd5e1",
                            borderRadius: "6px",
                          }}
                        />
                        <View
                          style={{
                            width: "30px",
                            height: "16px",
                            background: "#cbd5e1",
                            borderRadius: "8px",
                          }}
                        />
                      </Flex>
                    </View>
                  ))}
                </Flex>
              </View>
            ) : stats.skillPackages > 0 ? (
              <View textAlign="center" width="100%">
                <View
                  style={{
                    perspective: "1000px",
                    width: "100%",
                    height: "240px",
                    marginBottom: "1.5rem",
                  }}
                >
                  <View
                    style={{
                      position: "relative",
                      width: "100%",
                      height: "100%",
                      transformStyle: "preserve-3d",
                      transition: "transform 0.6s",
                      cursor: "pointer",
                    }}
                    onClick={() => navigate("/app/skillpackages/discover")}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = "rotateY(180deg)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = "rotateY(0deg)";
                    }}
                  >
                    {/* Front Side */}
                    <View
                      style={{
                        position: "absolute",
                        width: "100%",
                        height: "100%",
                        backfaceVisibility: "hidden",
                        background:
                          "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                        borderRadius: "20px",
                        padding: "2rem 1rem",
                        boxShadow: "0 8px 25px rgba(102, 126, 234, 0.2)",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        textAlign: "center",
                      }}
                    >
                      <Text
                        fontSize="4rem"
                        fontWeight="bold"
                        color="white"
                        marginBottom="0.5rem"
                      >
                        {stats.skillPackages}
                      </Text>
                      <Text fontSize="1rem" color="rgba(255, 255, 255, 0.9)">
                        {stats.skillPackages === 1
                          ? t("dashboard.skillPackages.activePackage")
                          : t("dashboard.skillPackages.activePackages")}
                      </Text>
                    </View>

                    {/* Back Side */}
                    <View
                      style={{
                        position: "absolute",
                        width: "100%",
                        height: "100%",
                        backfaceVisibility: "hidden",
                        background:
                          "linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)",
                        borderRadius: "20px",
                        padding: "2rem 1rem",
                        boxShadow: "0 8px 25px rgba(6, 182, 212, 0.2)",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        textAlign: "center",
                        transform: "rotateY(180deg)",
                      }}
                    >
                      <div
                        style={{
                          fontSize: "2.5rem",
                          marginBottom: "1rem",
                          padding: "1rem",
                          borderRadius: "50%",
                          background: "rgba(255, 255, 255, 0.2)",
                          width: "80px",
                          height: "80px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <span style={{ filter: "brightness(0) invert(1)" }}>
                          📚
                        </span>
                      </div>
                      <Text
                        fontSize="1.2rem"
                        fontWeight="bold"
                        color="white"
                        marginBottom="0.5rem"
                      >
                        {t("dashboard.skillPackages.addPackage.title")}
                      </Text>
                      <Text
                        fontSize="0.85rem"
                        color="rgba(255, 255, 255, 0.9)"
                        marginBottom="1rem"
                      >
                        {t("dashboard.skillPackages.addPackage.description")}
                      </Text>
                      <Text
                        fontSize="0.9rem"
                        color="white"
                        style={{ textDecoration: "underline" }}
                      >
                        {t("dashboard.skillPackages.addPackage.action")}
                      </Text>
                    </View>
                  </View>
                </View>

                <Flex direction="column" gap="1rem">
                  <View
                    className="drop-in-1"
                    style={{
                      background:
                        "linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)",
                      borderRadius: "12px",
                      padding: "1rem",
                      border: "1px solid #e2e8f0",
                    }}
                  >
                    <Flex justifyContent="space-between" alignItems="center">
                      <Text
                        fontSize="0.9rem"
                        color="var(--amplify-colors-font-secondary)"
                      >
                        {t("dashboard.skillPackages.stats.categories")}
                      </Text>
                      <Text fontSize="1.2rem" fontWeight="bold" color="#667eea">
                        {stats.skillCategories}
                      </Text>
                    </Flex>
                  </View>

                  <View
                    className="drop-in-2"
                    style={{
                      background:
                        "linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)",
                      borderRadius: "12px",
                      padding: "1rem",
                      border: "1px solid #e2e8f0",
                    }}
                  >
                    <Flex justifyContent="space-between" alignItems="center">
                      <Text
                        fontSize="0.9rem"
                        color="var(--amplify-colors-font-secondary)"
                      >
                        {t("dashboard.skillPackages.stats.skillsAvailable")}
                      </Text>
                      <Text fontSize="1.2rem" fontWeight="bold" color="#667eea">
                        {stats.skills}
                      </Text>
                    </Flex>
                  </View>

                  <View
                    className="drop-in-3"
                    style={{
                      background:
                        "linear-gradient(135deg, #10b981 0%, #059669 100%)",
                      borderRadius: "12px",
                      padding: "1rem",
                      boxShadow: "0 4px 15px rgba(16, 185, 129, 0.2)",
                    }}
                  >
                    <Flex justifyContent="space-between" alignItems="center">
                      <Text fontSize="0.9rem" color="rgba(255, 255, 255, 0.9)">
                        {t("dashboard.skillPackages.stats.skillsRated")}
                      </Text>
                      <Text fontSize="1.2rem" fontWeight="bold" color="white">
                        {stats.ratedSkills}
                      </Text>
                    </Flex>
                  </View>

                  <View
                    className="drop-in-4"
                    style={{
                      background:
                        "linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)",
                      borderRadius: "12px",
                      padding: "1rem",
                      boxShadow: "0 4px 15px rgba(139, 92, 246, 0.2)",
                    }}
                  >
                    <Flex justifyContent="space-between" alignItems="center">
                      <Text fontSize="0.9rem" color="rgba(255, 255, 255, 0.9)">
                        {t("dashboard.skillPackages.stats.packagesCompleted")}
                      </Text>
                      <Text fontSize="1.2rem" fontWeight="bold" color="white">
                        {stats.completionPercentage}%
                      </Text>
                    </Flex>
                  </View>
                </Flex>
              </View>
            ) : (
              <View textAlign="center" width="100%">
                <View
                  style={{
                    perspective: "1000px",
                    width: "100%",
                    height: "240px",
                    marginBottom: "1.5rem",
                  }}
                >
                  <View
                    style={{
                      position: "relative",
                      width: "100%",
                      height: "100%",
                      transformStyle: "preserve-3d",
                      transition: "transform 0.6s",
                      cursor: "pointer",
                    }}
                    onClick={() => navigate("/app/skillpackages/discover")}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = "rotateY(180deg)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = "rotateY(0deg)";
                    }}
                  >
                    {/* Front Side */}
                    <View
                      style={{
                        position: "absolute",
                        width: "100%",
                        height: "100%",
                        backfaceVisibility: "hidden",
                        background:
                          "linear-gradient(135deg, #6b7280 0%, #4b5563 100%)",
                        borderRadius: "20px",
                        padding: "2rem 1rem",
                        boxShadow: "0 8px 25px rgba(107, 114, 128, 0.2)",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        textAlign: "center",
                      }}
                    >
                      <div
                        style={{
                          fontSize: "2.5rem",
                          marginBottom: "1rem",
                          padding: "1rem",
                          borderRadius: "50%",
                          background: "rgba(255, 255, 255, 0.2)",
                          width: "80px",
                          height: "80px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <span style={{ filter: "brightness(0) invert(1)" }}>
                          📊
                        </span>
                      </div>
                      <Text
                        fontSize="1.2rem"
                        fontWeight="bold"
                        color="white"
                        marginBottom="0.5rem"
                      >
                        {t("dashboard.skillPackages.getStarted.title")}
                      </Text>
                      <Text fontSize="0.9rem" color="rgba(255, 255, 255, 0.9)">
                        {t("dashboard.skillPackages.getStarted.description")}
                      </Text>
                    </View>

                    {/* Back Side */}
                    <View
                      style={{
                        position: "absolute",
                        width: "100%",
                        height: "100%",
                        backfaceVisibility: "hidden",
                        background:
                          "linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)",
                        borderRadius: "20px",
                        padding: "2rem 1rem",
                        boxShadow: "0 8px 25px rgba(6, 182, 212, 0.2)",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        textAlign: "center",
                        transform: "rotateY(180deg)",
                      }}
                    >
                      <div
                        style={{
                          fontSize: "2.5rem",
                          marginBottom: "1rem",
                          padding: "1rem",
                          borderRadius: "50%",
                          background: "rgba(255, 255, 255, 0.2)",
                          width: "80px",
                          height: "80px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <span style={{ filter: "brightness(0) invert(1)" }}>
                          📚
                        </span>
                      </div>
                      <Text
                        fontSize="1.2rem"
                        fontWeight="bold"
                        color="white"
                        marginBottom="0.5rem"
                      >
                        Add Skill Package
                      </Text>
                      <Text
                        fontSize="0.85rem"
                        color="rgba(255, 255, 255, 0.9)"
                        marginBottom="1rem"
                      >
                        {t(
                          "dashboard.skillPackages.addPackage.descriptionLong",
                        )}
                      </Text>
                      <Text
                        fontSize="0.9rem"
                        color="white"
                        style={{ textDecoration: "underline" }}
                      >
                        {t("dashboard.skillPackages.addPackage.action")}
                      </Text>
                    </View>
                  </View>
                </View>
              </View>
            )}
          </Flex>
        </View>

        {/* Certifications Widget */}
        <View flex="1" minWidth="300px">
          <Flex
            direction="column"
            alignItems="center"
            justifyContent="flex-start"
            height="100%"
          >
            {loading ? (
              <View textAlign="center" width="100%">
                <View
                  style={{
                    width: "100%",
                    height: "240px",
                    marginBottom: "1.5rem",
                    background:
                      "linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)",
                    borderRadius: "20px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    animation: "pulse 2s infinite",
                  }}
                >
                  <Text color="var(--amplify-colors-font-secondary)">
                    {t("dashboard.loading.certifications")}
                  </Text>
                </View>
              </View>
            ) : stats.certifications > 0 ? (
              <View textAlign="center" width="100%">
                <View
                  style={{
                    perspective: "1000px",
                    width: "100%",
                    height: "240px",
                    marginBottom: "1.5rem",
                  }}
                >
                  <View
                    style={{
                      position: "relative",
                      width: "100%",
                      height: "100%",
                      transformStyle: "preserve-3d",
                      transition: "transform 0.6s",
                      cursor: "pointer",
                    }}
                    onClick={() => navigate("/app/certifications/add")}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = "rotateY(180deg)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = "rotateY(0deg)";
                    }}
                  >
                    {/* Front Side */}
                    <View
                      style={{
                        position: "absolute",
                        width: "100%",
                        height: "100%",
                        backfaceVisibility: "hidden",
                        background:
                          "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                        borderRadius: "20px",
                        padding: "2rem 1rem",
                        boxShadow: "0 8px 25px rgba(245, 158, 11, 0.2)",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        textAlign: "center",
                      }}
                    >
                      <Text
                        fontSize="4rem"
                        fontWeight="bold"
                        color="white"
                        marginBottom="0.5rem"
                      >
                        {stats.certifications}
                      </Text>
                      <Text fontSize="1rem" color="rgba(255, 255, 255, 0.9)">
                        {stats.certifications === 1
                          ? t("dashboard.certifications.certification")
                          : t("dashboard.certifications.certifications")}
                      </Text>
                    </View>

                    {/* Back Side */}
                    <View
                      style={{
                        position: "absolute",
                        width: "100%",
                        height: "100%",
                        backfaceVisibility: "hidden",
                        background:
                          "linear-gradient(135deg, #10b981 0%, #059669 100%)",
                        borderRadius: "20px",
                        padding: "2rem 1rem",
                        boxShadow: "0 8px 25px rgba(16, 185, 129, 0.2)",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        textAlign: "center",
                        transform: "rotateY(180deg)",
                      }}
                    >
                      <div
                        style={{
                          fontSize: "2.5rem",
                          marginBottom: "1rem",
                          padding: "1rem",
                          borderRadius: "50%",
                          background: "rgba(255, 255, 255, 0.2)",
                          width: "80px",
                          height: "80px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <span style={{ filter: "brightness(0) invert(1)" }}>
                          🏆
                        </span>
                      </div>
                      <Text
                        fontSize="1.2rem"
                        fontWeight="bold"
                        color="white"
                        marginBottom="0.5rem"
                      >
                        Add Certification
                      </Text>
                      <Text
                        fontSize="0.85rem"
                        color="rgba(255, 255, 255, 0.9)"
                        marginBottom="1rem"
                      >
                        Showcase your professional achievements and credentials
                      </Text>
                      <Text
                        fontSize="0.9rem"
                        color="white"
                        style={{ textDecoration: "underline" }}
                      >
                        Click to Add →
                      </Text>
                    </View>
                  </View>
                </View>

                {/* Certification Expiration Alert */}
                {expiringCerts.length > 0 && (
                  <View
                    className="cert-alert"
                    style={{
                      background:
                        "linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)",
                      borderRadius: "12px",
                      padding: "1rem",
                      border: "1px solid #e2e8f0",
                      cursor: "pointer",
                    }}
                    onClick={() => navigate("/app/certifications")}
                  >
                    <Flex justifyContent="space-between" alignItems="center">
                      <Text
                        fontSize="0.9rem"
                        color="var(--amplify-colors-font-secondary)"
                      >
                        {t("dashboard.certifications.expiringCerts.title")}
                      </Text>
                      <Text fontSize="1.2rem" fontWeight="bold" color="#f59e0b">
                        {expiringCerts.length}
                      </Text>
                    </Flex>
                  </View>
                )}
              </View>
            ) : (
              <View textAlign="center" width="100%">
                <View
                  style={{
                    perspective: "1000px",
                    width: "100%",
                    height: "240px",
                    marginBottom: "1.5rem",
                  }}
                >
                  <View
                    style={{
                      position: "relative",
                      width: "100%",
                      height: "100%",
                      transformStyle: "preserve-3d",
                      transition: "transform 0.6s",
                      cursor: "pointer",
                    }}
                    onClick={() => navigate("/app/certifications/add")}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = "rotateY(180deg)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = "rotateY(0deg)";
                    }}
                  >
                    {/* Front Side */}
                    <View
                      style={{
                        position: "absolute",
                        width: "100%",
                        height: "100%",
                        backfaceVisibility: "hidden",
                        background:
                          "linear-gradient(135deg, #6b7280 0%, #4b5563 100%)",
                        borderRadius: "20px",
                        padding: "2rem 1rem",
                        boxShadow: "0 8px 25px rgba(107, 114, 128, 0.2)",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        textAlign: "center",
                      }}
                    >
                      <div
                        style={{
                          fontSize: "2.5rem",
                          marginBottom: "1rem",
                          padding: "1rem",
                          borderRadius: "50%",
                          background: "rgba(255, 255, 255, 0.2)",
                          width: "80px",
                          height: "80px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <span style={{ filter: "brightness(0) invert(1)" }}>
                          🎓
                        </span>
                      </div>
                      <Text
                        fontSize="1.2rem"
                        fontWeight="bold"
                        color="white"
                        marginBottom="0.5rem"
                      >
                        {t("dashboard.certifications.showcase.title")}
                      </Text>
                      <Text fontSize="0.9rem" color="rgba(255, 255, 255, 0.9)">
                        {t("dashboard.certifications.showcase.description")}
                      </Text>
                    </View>

                    {/* Back Side */}
                    <View
                      style={{
                        position: "absolute",
                        width: "100%",
                        height: "100%",
                        backfaceVisibility: "hidden",
                        background:
                          "linear-gradient(135deg, #10b981 0%, #059669 100%)",
                        borderRadius: "20px",
                        padding: "2rem 1rem",
                        boxShadow: "0 8px 25px rgba(16, 185, 129, 0.2)",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        textAlign: "center",
                        transform: "rotateY(180deg)",
                      }}
                    >
                      <div
                        style={{
                          fontSize: "2.5rem",
                          marginBottom: "1rem",
                          padding: "1rem",
                          borderRadius: "50%",
                          background: "rgba(255, 255, 255, 0.2)",
                          width: "80px",
                          height: "80px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <span style={{ filter: "brightness(0) invert(1)" }}>
                          🏆
                        </span>
                      </div>
                      <Text
                        fontSize="1.2rem"
                        fontWeight="bold"
                        color="white"
                        marginBottom="0.5rem"
                      >
                        Add Certification
                      </Text>
                      <Text
                        fontSize="0.85rem"
                        color="rgba(255, 255, 255, 0.9)"
                        marginBottom="1rem"
                      >
                        Showcase your professional achievements and credentials
                      </Text>
                      <Text
                        fontSize="0.9rem"
                        color="white"
                        style={{ textDecoration: "underline" }}
                      >
                        Click to Add →
                      </Text>
                    </View>
                  </View>
                </View>
              </View>
            )}
          </Flex>
        </View>

        {/* Share Skills Widget - Only show if pro and has skills */}
        {isPro && stats.skillPackages > 0 && (
          <View flex="1" minWidth="300px">
            <Flex
              direction="column"
              alignItems="center"
              justifyContent="flex-start"
              height="100%"
            >
              {loading ? (
                <View textAlign="center" width="100%">
                  <View
                    style={{
                      width: "100%",
                      height: "240px",
                      marginBottom: "1.5rem",
                      background:
                        "linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)",
                      borderRadius: "20px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      animation: "pulse 2s infinite",
                    }}
                  >
                    <Text color="var(--amplify-colors-font-secondary)">
                      {t("dashboard.loading.publicPage")}
                    </Text>
                  </View>
                </View>
              ) : (
                <View textAlign="center" width="100%">
                  <View
                    style={{
                      perspective: "1000px",
                      width: "100%",
                      height: "240px",
                      marginBottom: "1.5rem",
                    }}
                  >
                    <View
                      ref={(el) => {
                        if (el) {
                          const isWide = el.offsetWidth > 600;
                          el.setAttribute("data-wide", isWide.toString());
                        }
                      }}
                      style={{
                        position: "relative",
                        width: "100%",
                        height: "100%",
                        transformStyle: "preserve-3d",
                        transition: "transform 0.6s",
                        cursor: "pointer",
                      }}
                      onClick={() =>
                        navigate("/settings/sharing", {
                          state: { tab: "invitations" },
                        })
                      }
                      onMouseEnter={(e) => {
                        const isWide =
                          e.currentTarget.getAttribute("data-wide") === "true";
                        if (isWide) {
                          e.currentTarget.style.transform =
                            "scale(1.02) translateY(-5px)";
                        } else {
                          e.currentTarget.style.transform = "rotateY(180deg)";
                        }
                      }}
                      onMouseLeave={(e) => {
                        const isWide =
                          e.currentTarget.getAttribute("data-wide") === "true";
                        if (isWide) {
                          e.currentTarget.style.transform =
                            "scale(1) translateY(0)";
                        } else {
                          e.currentTarget.style.transform = "rotateY(0deg)";
                        }
                      }}
                    >
                      {/* Front Side */}
                      <View
                        style={{
                          position: "absolute",
                          width: "100%",
                          height: "100%",
                          backfaceVisibility: "hidden",
                          background:
                            "linear-gradient(135deg, #ec4899 0%, #be185d 100%)",
                          borderRadius: "20px",
                          padding: "2rem 1rem",
                          boxShadow: "0 8px 25px rgba(236, 72, 153, 0.2)",
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "center",
                          textAlign: "center",
                        }}
                      >
                        <Text
                          fontSize="4rem"
                          fontWeight="bold"
                          color="white"
                          marginBottom="0.5rem"
                        >
                          {stats.sharedSkills}
                        </Text>
                        <Text fontSize="1rem" color="rgba(255, 255, 255, 0.9)">
                          {t("dashboard.sharing.skillsShared")}
                        </Text>
                      </View>

                      {/* Back Side */}
                      <View
                        style={{
                          position: "absolute",
                          width: "100%",
                          height: "100%",
                          backfaceVisibility: "hidden",
                          background:
                            "linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)",
                          borderRadius: "20px",
                          padding: "2rem 1rem",
                          boxShadow: "0 8px 25px rgba(139, 92, 246, 0.2)",
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "center",
                          textAlign: "center",
                          transform: "rotateY(180deg)",
                        }}
                      >
                        <div
                          style={{
                            fontSize: "2.5rem",
                            marginBottom: "1rem",
                            padding: "1rem",
                            borderRadius: "50%",
                            background: "rgba(255, 255, 255, 0.2)",
                            width: "80px",
                            height: "80px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <span style={{ filter: "brightness(0) invert(1)" }}>
                            📤
                          </span>
                        </div>
                        <Text
                          fontSize="1.2rem"
                          fontWeight="bold"
                          color="white"
                          marginBottom="0.5rem"
                        >
                          {t("dashboard.sharing.manageInvitations.title")}
                        </Text>
                        <Text
                          fontSize="0.85rem"
                          color="rgba(255, 255, 255, 0.9)"
                          marginBottom="1rem"
                        >
                          {t("dashboard.sharing.manageInvitations.description")}
                        </Text>
                        <Text
                          fontSize="0.9rem"
                          color="white"
                          style={{ textDecoration: "underline" }}
                        >
                          {t("dashboard.sharing.manageInvitations.action")}
                        </Text>
                      </View>
                    </View>
                  </View>

                  {/* Pending Shares Sub-tile */}
                  {stats.pendingShares > 0 && (
                    <View
                      className="drop-in-4"
                      style={{
                        background:
                          "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                        borderRadius: "8px",
                        padding: "0.75rem",
                        cursor: "pointer",
                      }}
                      onClick={() =>
                        navigate("/settings/sharing", {
                          state: { tab: "invitations" },
                        })
                      }
                    >
                      <Flex justifyContent="space-between" alignItems="center">
                        <Text
                          fontSize="0.8rem"
                          color="rgba(255, 255, 255, 0.9)"
                        >
                          {t("dashboard.sharing.pendingShares")}
                        </Text>
                        <Text fontSize="1rem" fontWeight="bold" color="white">
                          {stats.pendingShares}
                        </Text>
                      </Flex>
                    </View>
                  )}
                </View>
              )}
            </Flex>
          </View>
        )}

        {/* Public Page Widget */}
        <View flex="1" minWidth="300px">
          <Flex
            direction="column"
            alignItems="center"
            justifyContent="flex-start"
            height="100%"
          >
            {loading ? (
              <View textAlign="center" width="100%">
                <View
                  style={{
                    width: "100%",
                    height: "240px",
                    marginBottom: "1.5rem",
                    background:
                      "linear-gradient(135deg, #f3f4f6 0%, #d1d5db 100%)",
                    borderRadius: "20px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    animation: "pulse 2s infinite",
                  }}
                >
                  <Text color="var(--amplify-colors-font-secondary)">
                    {t("dashboard.loading.sharing")}
                  </Text>
                </View>
              </View>
            ) : (
              <View textAlign="center" width="100%">
                <View
                  style={{
                    perspective: "1000px",
                    width: "100%",
                    height: "240px",
                  }}
                >
                  <style>{`
                    [data-wide="true"] {
                      height: 120px !important;
                    }
                  `}</style>
                  <View
                    ref={(el) => {
                      if (el) {
                        const isWide = el.offsetWidth > 600;
                        el.setAttribute("data-wide", isWide.toString());
                      }
                    }}
                    style={{
                      position: "relative",
                      width: "100%",
                      height: "100%",
                      transformStyle: "preserve-3d",
                      transition: "transform 0.6s",
                      cursor: "pointer",
                    }}
                    onClick={() => navigate("/settings/sharing")}
                    onMouseEnter={(e) => {
                      const isWide =
                        e.currentTarget.getAttribute("data-wide") === "true";
                      if (isWide) {
                        e.currentTarget.style.transform =
                          "scale(1.02) translateY(-5px)";
                      } else {
                        e.currentTarget.style.transform = "rotateY(180deg)";
                      }
                    }}
                    onMouseLeave={(e) => {
                      const isWide =
                        e.currentTarget.getAttribute("data-wide") === "true";
                      if (isWide) {
                        e.currentTarget.style.transform =
                          "scale(1) translateY(0)";
                      } else {
                        e.currentTarget.style.transform = "rotateY(0deg)";
                      }
                    }}
                  >
                    {/* Front Side - or combined view for wide tiles */}
                    <View
                      ref={(el) => {
                        if (el) {
                          const parent = el.closest('[data-wide="true"]');
                          if (parent) {
                            el.style.flexDirection = "row";
                            el.style.alignItems = "center";
                            el.style.textAlign = "left";
                          } else {
                            el.style.flexDirection = "column";
                            el.style.alignItems = "center";
                            el.style.textAlign = "center";
                          }
                        }
                      }}
                      style={{
                        position: "absolute",
                        width: "100%",
                        height: "100%",
                        backfaceVisibility: "hidden",
                        background: publicShare.enabled
                          ? "linear-gradient(135deg, #10b981 0%, #059669 100%)"
                          : "linear-gradient(135deg, #6b7280 0%, #4b5563 100%)",
                        borderRadius: "20px",
                        padding: "1.5rem",
                        boxShadow: publicShare.enabled
                          ? "0 8px 25px rgba(16, 185, 129, 0.2)"
                          : "0 8px 25px rgba(107, 114, 128, 0.2)",
                        display: "flex",
                        justifyContent: "center",
                        gap: "1rem",
                      }}
                    >
                      <div
                        style={{
                          fontSize: "2.5rem",
                          padding: "1rem",
                          borderRadius: "50%",
                          background: "rgba(255, 255, 255, 0.2)",
                          width: "80px",
                          height: "80px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <span style={{ filter: "brightness(0) invert(1)" }}>
                          {publicShare.enabled ? "🌐" : "🔒"}
                        </span>
                      </div>
                      <View flex="1">
                        <Text
                          fontSize="1.2rem"
                          fontWeight="bold"
                          color="white"
                          marginBottom="0.25rem"
                        >
                          {t("dashboard.publicPage.title")}
                        </Text>
                        <Text
                          fontSize="0.9rem"
                          color="rgba(255, 255, 255, 0.9)"
                        >
                          {publicShare.enabled
                            ? t("dashboard.publicPage.active")
                            : t("dashboard.publicPage.disabled")}
                        </Text>
                      </View>

                      {/* Show back content inline for wide tiles */}
                      <View
                        ref={(el) => {
                          if (el) {
                            const parent = el.closest('[data-wide="true"]');
                            el.style.display = parent ? "flex" : "none";
                          }
                        }}
                        style={{
                          alignItems: "center",
                          gap: "1rem",
                          borderLeft: "1px solid rgba(255, 255, 255, 0.3)",
                          paddingLeft: "1rem",
                          marginLeft: "1rem",
                        }}
                      >
                        {publicShare.enabled ? (
                          <>
                            <canvas
                              ref={(canvas) => {
                                if (canvas && publicShare.url) {
                                  QRCode.toCanvas(
                                    canvas,
                                    publicShare.url,
                                    { width: 60 },
                                    () => {
                                      // Ignore QR code errors
                                    },
                                  );
                                }
                              }}
                              width={60}
                              height={60}
                            />
                            <View>
                              <Text
                                fontSize="0.9rem"
                                color="white"
                                marginBottom="0.25rem"
                              >
                                {t("dashboard.publicPage.qrCode.scan")}
                              </Text>
                              <Text
                                fontSize="0.8rem"
                                color="rgba(255, 255, 255, 0.9)"
                              >
                                {t("dashboard.publicPage.qrCode.manage")}
                              </Text>
                            </View>
                          </>
                        ) : (
                          <>
                            <Text fontSize="2rem">⚙️</Text>
                            <View>
                              <Text
                                fontSize="1rem"
                                fontWeight="bold"
                                color="white"
                                marginBottom="0.25rem"
                              >
                                {t("dashboard.publicPage.enable.title")}
                              </Text>
                              <Text
                                fontSize="0.8rem"
                                color="rgba(255, 255, 255, 0.9)"
                              >
                                {t("dashboard.publicPage.enable.description")}
                              </Text>
                            </View>
                          </>
                        )}
                      </View>
                    </View>

                    {/* Back Side */}
                    <View
                      style={{
                        position: "absolute",
                        width: "100%",
                        height: "100%",
                        backfaceVisibility: "hidden",
                        background: publicShare.enabled
                          ? "linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)"
                          : "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                        borderRadius: "20px",
                        padding: "1rem",
                        boxShadow: publicShare.enabled
                          ? "0 8px 25px rgba(59, 130, 246, 0.2)"
                          : "0 8px 25px rgba(245, 158, 11, 0.2)",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        textAlign: "center",
                        transform: "rotateY(180deg)",
                      }}
                    >
                      {publicShare.enabled ? (
                        <>
                          <canvas
                            ref={(canvas) => {
                              if (canvas && publicShare.url) {
                                QRCode.toCanvas(
                                  canvas,
                                  publicShare.url,
                                  { width: 80 },
                                  () => {
                                    // Ignore QR code errors
                                  },
                                );
                              }
                            }}
                            width={80}
                            height={80}
                            style={{ marginBottom: "0.5rem" }}
                          />
                          <Text
                            fontSize="0.8rem"
                            color="white"
                            marginBottom="0.5rem"
                          >
                            {t("dashboard.publicPage.qrCode.scan")}
                          </Text>
                          <Text
                            fontSize="0.7rem"
                            color="rgba(255, 255, 255, 0.9)"
                            style={{ textDecoration: "underline" }}
                          >
                            {t("dashboard.publicPage.qrCode.manage")}
                          </Text>
                        </>
                      ) : (
                        <>
                          <div
                            style={{
                              fontSize: "2.5rem",
                              marginBottom: "1rem",
                              padding: "1rem",
                              borderRadius: "50%",
                              background: "rgba(255, 255, 255, 0.2)",
                              width: "80px",
                              height: "80px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <span style={{ filter: "brightness(0) invert(1)" }}>
                              ⚙️
                            </span>
                          </div>
                          <Text
                            fontSize="1.1rem"
                            fontWeight="bold"
                            color="white"
                            marginBottom="0.5rem"
                          >
                            {t("dashboard.publicPage.enable.title")}
                          </Text>
                          <Text
                            fontSize="0.8rem"
                            color="rgba(255, 255, 255, 0.9)"
                            marginBottom="1rem"
                          >
                            {t("dashboard.publicPage.enable.description")}
                          </Text>
                          <Text
                            fontSize="0.8rem"
                            color="white"
                            style={{ textDecoration: "underline" }}
                          >
                            {t("dashboard.publicPage.enable.action")}
                          </Text>
                        </>
                      )}
                    </View>
                  </View>
                </View>
              </View>
            )}
          </Flex>
        </View>
      </Flex>
    </PageContainer>
  );
}

export default Home;
